import React from "react";
import type { DateChangePlan, DateChangeImpact, StartOp, EndOp } from "../../types/offer-date-change.types";

export interface DateChangesModalProps {
  plan: DateChangePlan;
  impact: DateChangeImpact;
  onConfirm: () => void;
  onClose: () => void;
  loading: boolean;
  error: string | null;
}

function hasTrim(startOp: StartOp, endOp: EndOp): boolean {
  return startOp.kind === 'TRIM_START' || endOp.kind === 'TRIM_END';
}

function pluralizeDni(days: number): string {
  if (days === 1) return 'dzień';
  return 'dni';
}

function pluralizeExtend(n: number): string {
  return n === 1 ? 'zostanie dodany' : 'zostaną dodane';
}

function pluralizePuste(n: number): string {
  return n === 1 ? 'pusty dzień' : 'puste dni';
}

const DateChangesModal: React.FC<DateChangesModalProps> = ({
  plan,
  impact,
  onConfirm,
  onClose,
  loading,
  error,
}) => {
  const dialogRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.focus();
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !loading) {
      onClose();
      return;
    }
    if (e.key === 'Tab' && dialogRef.current) {
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const focusableArr = Array.from(focusable);
      if (focusableArr.length === 0) return;
      const first = focusableArr[0];
      const last = focusableArr[focusableArr.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  };

  // NO_OP and EXTEND-only should never open this modal — parent is responsible,
  // but guard defensively so modal renders nothing unexpected.
  if (plan.kind === 'NO_OP') return null;
  if (
    plan.kind === 'COMPOSITE' &&
    plan.startOp.kind !== 'TRIM_START' &&
    plan.endOp.kind !== 'TRIM_END'
  ) {
    return null;
  }

  let bodyContent: React.ReactNode;
  let ctaLabel: string;
  let ctaDestructive: boolean;

  if (plan.kind === 'FULL_SHIFT') {
    bodyContent = (
      <p className="text-gray-700 text-sm leading-relaxed">
        Termin zostanie przesunięty o{' '}
        <strong>{plan.shiftDays > 0 ? `+${plan.shiftDays}` : plan.shiftDays}</strong>{' '}
        {pluralizeDni(Math.abs(plan.shiftDays))}.{' '}
        Plan pobytu ({impact.planItemsPreserved} aktywności) zostanie zachowany.{' '}
        Wszystkie przypisania pokoi ({impact.accommodationsLost} pozycji) zostaną usunięte.
      </p>
    );
    ctaLabel = 'Przesuń termin i usuń przypisania pokoi';
    ctaDestructive = true;
  } else {
    // COMPOSITE
    const { startOp, endOp } = plan;

    const trimStartDays = startOp.kind === 'TRIM_START' ? startOp.days : 0;
    const trimEndDays = endOp.kind === 'TRIM_END' ? endOp.days : 0;
    const extendStartDays = startOp.kind === 'EXTEND_START' ? startOp.days : 0;
    const extendEndDays = endOp.kind === 'EXTEND_END' ? endOp.days : 0;

    const destructive = hasTrim(startOp, endOp);

    const lines: React.ReactNode[] = [];

    if (trimStartDays > 0 && trimEndDays > 0) {
      lines.push(
        <p key="trim-both" className="text-gray-700 text-sm leading-relaxed">
          Pierwsze <strong>{trimStartDays}</strong> {pluralizeDni(trimStartDays)} i ostatnie{' '}
          <strong>{trimEndDays}</strong> {pluralizeDni(trimEndDays)} zostaną usunięte.{' '}
          Stracisz: <strong>{impact.planItemsLost}</strong> pozycji planu pobytu
          {impact.accommodationsLost > 0 && (
            <> + <strong>{impact.accommodationsLost}</strong> noclegów</>
          )}
          .
        </p>
      );
    } else if (trimStartDays > 0) {
      lines.push(
        <p key="trim-start" className="text-gray-700 text-sm leading-relaxed">
          Pierwsze <strong>{trimStartDays}</strong> {pluralizeDni(trimStartDays)} zostanie usunięte.{' '}
          Stracisz: <strong>{impact.planItemsLost}</strong> pozycji planu pobytu
          {impact.accommodationsLost > 0 && (
            <> + <strong>{impact.accommodationsLost}</strong> noclegów</>
          )}
          .
        </p>
      );
    } else if (trimEndDays > 0) {
      lines.push(
        <p key="trim-end" className="text-gray-700 text-sm leading-relaxed">
          Ostatnie <strong>{trimEndDays}</strong> {pluralizeDni(trimEndDays)} zostanie usunięte.{' '}
          Stracisz: <strong>{impact.planItemsLost}</strong> pozycji planu pobytu
          {impact.accommodationsLost > 0 && (
            <> + <strong>{impact.accommodationsLost}</strong> noclegów</>
          )}
          .
        </p>
      );
    }

    if (extendStartDays > 0) {
      lines.push(
        <p key="extend-start" className="text-gray-700 text-sm leading-relaxed">
          Na początku {pluralizeExtend(extendStartDays)}{' '}
          <strong>{extendStartDays}</strong> {pluralizePuste(extendStartDays)}.
        </p>
      );
    }

    if (extendEndDays > 0) {
      lines.push(
        <p key="extend-end" className="text-gray-700 text-sm leading-relaxed">
          Na końcu {pluralizeExtend(extendEndDays)}{' '}
          <strong>{extendEndDays}</strong> {pluralizePuste(extendEndDays)}.
        </p>
      );
    }

    bodyContent = <div className="space-y-2">{lines}</div>;
    const isSoloTrimStart = trimStartDays > 0 && trimEndDays === 0 && extendStartDays === 0 && extendEndDays === 0;
    const isSoloTrimEnd = trimEndDays > 0 && trimStartDays === 0 && extendStartDays === 0 && extendEndDays === 0;
    if (isSoloTrimStart) {
      ctaLabel = `Usuń ${trimStartDays} ${pluralizeDni(trimStartDays)} i zapisz`;
    } else if (isSoloTrimEnd) {
      ctaLabel = `Usuń ${trimEndDays} ${pluralizeDni(trimEndDays)} i zapisz`;
    } else {
      ctaLabel = 'Zastosuj zmiany';
    }
    ctaDestructive = destructive;
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="date-changes-modal-title"
      aria-describedby="date-changes-modal-description"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      ref={dialogRef}
    >
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3
          id="date-changes-modal-title"
          className="text-lg font-semibold mb-4"
        >
          Potwierdzenie zmiany terminu
        </h3>

        <div
          id="date-changes-modal-description"
          className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6"
        >
          {bodyContent}
        </div>

        {error && (
          <p role="alert" className="text-red-600 text-sm mb-4">
            {error}
          </p>
        )}

        <div className="flex flex-col space-y-3">
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`w-full px-4 py-2 text-white rounded-md disabled:opacity-50 flex items-center justify-center gap-2 ${
              ctaDestructive
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading && (
              <span
                className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                aria-hidden="true"
              />
            )}
            {ctaLabel}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateChangesModal;
